import axios from 'axios';
import useLoading from 'hooks/useLoading';
import useSetTimeout from 'hooks/useSetTimeout';
import useTitle from 'hooks/useTitle';
import * as Upload from 'models/upload';
import React, { useMemo, useState } from 'react';
import {
  Alert, Button, Form, Spinner,
} from 'react-bootstrap';
import { useParams } from 'react-router-dom';

interface UploadState {
  token: string;
  file?: File;
}

type RouteParams = {
  uploadToken: string
};
export const SecureUpload = () => {
  useTitle('DigiTach: Sikker upload');
  const { uploadToken } = useParams<RouteParams>();

  const [state, setState] = useState<UploadState>({
    token: uploadToken ?? '',
  });
  const [tokenLocked, setTokenLocked] = useState(uploadToken !== undefined);
  const [loading, setLoading] = useLoading(false);
  const [inputKey, setInputKey] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);

  const [tokenInvalid, setTokenInvalid] = useState(false);
  const clearSuccess = useSetTimeout();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files !== null) {
      const { files } = event.target;
      setState((prev) => ({ ...prev, file: files[0] }));
    } else {
      setState((prev) => ({ ...prev, file: undefined }));
    }
  };

  const formSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!state.file) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', state.file, state.file.name);

      await Upload.api.Upload(formData, state.token);

      setInputKey((prev) => prev + 1);
      setShowSuccess(true);
      clearSuccess(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error(error);
      if (axios.isAxiosError(error)) {
        console.log(error.code);
        if (error.response?.status === 404) {
          setTokenInvalid(true);
          setTokenLocked(false);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const changeTokenKey = (value: string) => {
    setState((prev) => ({
      ...prev,
      token: value,
    }));
    if (tokenInvalid) {
      setTokenInvalid(false);
    }
  };

  // eslint-disable-next-line max-len
  const canUpload = useMemo(() => state.file !== undefined && state.token.length >= 32, [state.file, state.token]);

  return (
    <div className="upload-form">
      <h4>Sikker upload af filer</h4>
      <Alert variant="secondary">
        Upload sikkert følsomme filer der f.eks. indeholder person oplysninger, til os.
        <br />
        Det kunne eksempelvis være:
        <ul>
          <li>Chauffør liste til indkaldelse, som indeholder CPR-numre.</li>
        </ul>
      </Alert>

      <Alert variant="success">
        Alle filer sendes og opbevares stærkt krypteret således at kun vi kan tilgå dem.
      </Alert>
      <Form onSubmit={formSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Nøgle</Form.Label>
          <Form.Control
            id="token-key-input"
            type="text"
            name="token"
            placeholder="Indtast upload nøgle"
            value={state.token}
            onChange={(event) => changeTokenKey(event.target.value)}
            disabled={tokenLocked}
            isInvalid={tokenInvalid}
          />
          <Form.Control.Feedback type="invalid">
            Ugyldig upload nøgle
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Fil</Form.Label>
          <Form.Control
            key={inputKey}
            className="w-auto"
            type="file"
            name="file"
            placeholder="Vælg fil"
            onChange={handleFileChange}
          />
        </Form.Group>
        <Button variant="success" type="submit" disabled={!canUpload}>
          Upload fil
          {loading && <Spinner size="sm" animation="border" />}
        </Button>

        {showSuccess && <Alert variant="success">Filen blev uploadet!</Alert>}
      </Form>
    </div>
  );
};

export default SecureUpload;
